import { MpxSearchQuery, MpxTypeaheadAPI, MpxTypeaheadResult } from '@cibo/core'
import { useTypeaheadSuggestions } from '@cibo/ui'
import SearchIcon from '@mui/icons-material/Search'
import {
  Autocomplete,
  AutocompleteChangeReason,
  InputAdornment,
  TextField,
  useTheme,
} from '@mui/material'
import { SyntheticEvent, useState } from 'react'
import { TypeaheadResultRow } from './TypeaheadResultRow'

type LocationSearchProps = {
  onLocationSelected?: (bbox: Pick<MpxSearchQuery, 'lowerLeft' | 'upperRight'>) => void
}

const deBrace = (s: string) => s.replace(/\{\{/g, '').replace(/\}\}/g, '')

export const LocationSearch = ({ onLocationSelected }: LocationSearchProps) => {
  const { palette } = useTheme()
  const [searchText, setSearchText] = useState('')

  const onChangeText = (event: SyntheticEvent, value: string) => {
    if (event?.type === 'change') {
      setSearchText(value)
    }
  }

  const suggestions = useTypeaheadSuggestions({
    searchText,
    limit: 8,
    excludeParcelIdx: true,
    _f: undefined, // do not include plain text results
  })

  const handleResultSelect = (
    e: SyntheticEvent,
    suggestion: MpxTypeaheadResult | null | string,
    reason: AutocompleteChangeReason
  ) => {
    if (reason === 'clear') {
      return setSearchText('')
    }

    if (suggestion === null || typeof suggestion === 'string') return

    setSearchText(deBrace(suggestion.label))
    const { resultInd } = suggestion

    MpxTypeaheadAPI.resolveQuery({ resultInd }).then(result => {
      onLocationSelected && onLocationSelected(result)
    })
  }

  return (
    <Autocomplete
      sx={{
        backgroundColor: palette.background.paper,
        borderRadius: 1,
        height: '64px',
      }}
      style={{ pointerEvents: 'all' }}
      id="location-search"
      options={suggestions.data || []}
      onChange={handleResultSelect}
      filterOptions={x => x}
      renderOption={(props, option) => (
        <TypeaheadResultRow suggestion={option as MpxTypeaheadResult} {...props} />
      )}
      inputValue={searchText}
      renderInput={params => (
        <TextField
          {...params}
          label=""
          InputProps={{
            ...params.InputProps,
            sx: { height: '64px' },
            startAdornment: (
              <InputAdornment position="start" sx={{ marginLeft: 1 }}>
                <SearchIcon />
              </InputAdornment>
            ),
            // @ts-ignore this or browser warning
            disableunderline: 'true',
          }}
        />
      )}
      onInputChange={onChangeText}
      fullWidth={!!searchText && searchText !== ''}
    />
  )
}
